import React, { useEffect } from 'react';

import {
  useChildComponent,
  useMountedState,
  useProcessAsync,
} from '@gogame/hooks-ts';
import DataGrid from '@gogame/data-grid';
import {
  useScreenState,
  useScreenDispatch,
  useScreenTitle,
} from '@gogame/screens';
import { useSocket } from '@gogame/sockets';

import {
  COLUMN_FINAL,
  COLUMN_CURRENT,
  COLUMN_STATUS,
  COLUMN_COMMENT,
  COLUMN_DATE_UPDATED,
  COLUMN_AUTHOR,
  COLUMN_DATE_CREATED,
  EVENT_RELOAD_GRID,
} from 'scenes/MarketingPlanning/MarketingPlanningGrid/constants';
import { useLoadMarketingPlanningGrid } from 'scenes/MarketingPlanning/MarketingPlanningGrid/hooks';
import { getColor } from 'scenes/MarketingPlanning/MarketingPlanningGrid/components/Status';
import {
  SET_TABLE_SETTINGS,
  COLLAPSE_COLUMNS,
  REORDER_COLUMNS,
} from 'scenes/MarketingPlanning/MarketingPlanningGrid/screenReducer';

import { CurrentColumn, FinalColumn } from './Columns';
import Toolbar from './Toolbar';
import MarketingPlanningActions from 'scenes/MarketingPlanning/MarketingPlanningGrid/components/Actions';

const getRowId = ({ year, month }) => `${year}.${month}`;

const MarketingPlanningDataTable = ({
  onDiscard,
  onShowSummaryByOwners,
  onShowSummaryByManagers,
  onShowSummaryByDirectors,
  onShowHistory,
}) => {
  const [childComponent, openChildComponent] = useChildComponent();

  const [loading, processAsync] = useProcessAsync();
  const [data, setData] = useMountedState(null);
  const [screenTitle] = useScreenTitle();
  const loadMarketingPlanningGrid = useLoadMarketingPlanningGrid(processAsync);
  const screenState = useScreenState();
  const dispatch = useScreenDispatch();
  const socket = useSocket();

  const loadData = async () => {
    return loadMarketingPlanningGrid({
      tableSettings: screenState.tableSettings,
    }).then(setData);
  };

  useEffect(() => {
    loadData();
  }, [screenState.tableSettings]);

  useEffect(() => {
    socket.on(EVENT_RELOAD_GRID, loadData);
  }, []);

  const handleChangeSettings = (settings) => {
    dispatch({
      type: SET_TABLE_SETTINGS,
      payload: settings,
    });
  };

  const handleReorder = (order) => {
    dispatch({
      type: REORDER_COLUMNS,
      payload: order,
    });
  };

  const handleCollapse = (collapsed) => {
    dispatch({
      type: COLLAPSE_COLUMNS,
      payload: collapsed,
    });
  };

  return (
    <React.Fragment>
      <DataGrid
        rows={data?.rows}
        count={data?.count}
        title={screenTitle}
        loading={loading}
        onChangeSettings={handleChangeSettings}
        onReorder={handleReorder}
        onCollapse={handleCollapse}
        tableSettings={screenState.tableSettings}
        columnsState={screenState.columns}
        toolbar={<Toolbar loadData={loadData} />}
        storageKey={'marketingPlanning'}
        defaultColumnWidth={160}
        getRowId={getRowId}
      >
        <DataGrid.Column
          field={COLUMN_FINAL}
          headerName={'Last Finalized Version'}
          width={200}
          renderCell={({ row }) => <FinalColumn row={row} />}
        />
        <DataGrid.Column
          field={COLUMN_CURRENT}
          headerName={'Last Changes'}
          width={350}
          sortable={false}
          renderCell={({ row }) => (
            <CurrentColumn row={row} onDiscard={onDiscard} />
          )}
        />
        <DataGrid.Column
          field={COLUMN_STATUS}
          headerName={'Last Version Status'}
          type={'status'}
          getStatusColor={getColor}
          getValue={(value) => (value.id ? value : null)}
        />
        <DataGrid.Column
          field={COLUMN_COMMENT}
          headerName={'Comment'}
          type={'text'}
          minWidth={300}
          flex={1}
        />
        <DataGrid.Column
          field={COLUMN_DATE_UPDATED}
          headerName={'Last Updated'}
          width={180}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          field={COLUMN_AUTHOR}
          headerName={'Created by'}
          type={'text'}
        />
        <DataGrid.Column
          field={COLUMN_DATE_CREATED}
          headerName={'Created'}
          type={'date'}
          template={'LLL'}
          timeZone={'local'}
        />
        <DataGrid.Column
          actionsColDef
          type={'actions'}
          actionsComponent={MarketingPlanningActions}
          onShowSummaryByOwners={onShowSummaryByOwners}
          onShowSummaryByManagers={onShowSummaryByManagers}
          onShowSummaryByDirectors={onShowSummaryByDirectors}
          onShowHistory={onShowHistory}
          onSuccess={loadData}
          onHasErrors={loadData}
          onOpenChildComponent={openChildComponent}
          limit={3}
        />
      </DataGrid>
      {childComponent}
    </React.Fragment>
  );
};

export default MarketingPlanningDataTable;
